"use client";

import { CalendarDays, ChartColumn, Layers, Plus, Radar, Users, Waypoints } from "lucide-react";
import { cn, withAlpha, type CurrentUser, type Employee, type Team } from "@routecore/core";
import { ConfirmDelete, EmployeeDot } from "@routecore/controls";
import { RollingNumber } from "@routecore/motion-fx";

export type TabId = "calendar" | "today" | "reports" | "records";

export const NAV_ITEMS: { id: TabId; label: string; hint: string; icon: typeof CalendarDays }[] = [
  { id: "calendar", label: "Calendar", hint: "Month at a glance", icon: CalendarDays },
  { id: "today", label: "Today", hint: "Live status board", icon: Radar },
  { id: "reports", label: "Reports", hint: "Visual insights", icon: ChartColumn },
  { id: "records", label: "Records", hint: "Full history", icon: Layers },
];

export default function Sidebar({
  tab,
  onTab,
  employees,
  teams,
  itineraryCount,
  monthCount,
  currentUser,
  isPrivileged,
  isSuperAdmin,
  canEditEmployee,
  onAddEmployee,
  onEditEmployee,
  onDeleteEmployee,
  onManageTeams,
}: {
  tab: TabId;
  onTab: (t: TabId) => void;
  employees: Employee[];
  teams: Team[];
  itineraryCount: number;
  monthCount: number;
  currentUser: CurrentUser | null;
  isPrivileged: boolean;
  isSuperAdmin: boolean;
  canEditEmployee: (emp: Pick<Employee, "id" | "boundAccountId" | "boundAccountEmail">) => boolean;
  onAddEmployee: () => void;
  onEditEmployee: (e: Employee) => void;
  onDeleteEmployee: (id: number) => void;
  onManageTeams: () => void;
}) {
  return (
    <aside className="sticky top-5 hidden h-[calc(100vh-2.5rem)] w-[292px] shrink-0 flex-col overflow-hidden rounded-3xl border border-line bg-porcelain/85 shadow-lifted backdrop-blur-xl lg:flex 2xl:w-[308px]">
      {/* brand */}
      <div className="flex items-center gap-3 border-b border-line/70 px-5 pt-5 pb-4">
        <span className="grid size-11 shrink-0 place-items-center rounded-2xl bg-white shadow-card p-1.5">
          <img src="/iSource LOGO1.png" alt="iSource" className="w-full h-full object-contain" />
        </span>
        <div className="min-w-0">
          <div className="font-display text-lg leading-none font-semibold tracking-tight">
            iSource RouteCore
          </div>
          <div className="mt-1 text-[9px] leading-tight font-bold tracking-[0.16em] text-ink-faint uppercase">
            Team Itinerary System
          </div>
        </div>
      </div>

      {/* nav */}
      <nav className="space-y-1 px-3 pt-4">
        {NAV_ITEMS.map((item) => {
          const active = tab === item.id;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => onTab(item.id)}
              className={cn(
                "group flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-all active:scale-[0.99]",
                active ? "bg-ink text-porcelain shadow-card" : "text-ink hover:bg-black/[0.045]",
              )}
            >
              <span
                className={cn(
                  "grid size-8 shrink-0 place-items-center rounded-lg transition-colors",
                  active ? "bg-white/10 text-porcelain" : "bg-cream text-ink-soft group-hover:text-ink",
                )}
              >
                <item.icon className="size-4" />
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-bold">{item.label}</span>
                <span
                  className={cn(
                    "block truncate text-[11px]",
                    active ? "text-porcelain/60" : "text-ink-faint",
                  )}
                >
                  {item.hint}
                </span>
              </span>
            </button>
          );
        })}
      </nav>

      {/* roster */}
      <div className="mt-6 flex min-h-0 flex-1 flex-col px-3">
        <div className="flex items-center justify-between px-2 pb-2">
          <span className="text-[10px] font-bold tracking-[0.22em] text-ink-faint uppercase">
            Field team
          </span>
          <span className="rounded-full bg-cream px-2 py-0.5 text-[10px] font-bold text-ink-soft">
            {employees.length}
          </span>
        </div>

        {/* teams strip */}
        <div className="mb-2 rounded-xl border border-line bg-paper/60 p-2">
          <div className="flex flex-wrap items-center gap-1">
            {teams.map((t) => {
              const count = employees.filter((e) => e.teamId === t.id).length;
              return (
                <span
                  key={t.id}
                  title={`${count} ${count === 1 ? "member" : "members"}`}
                  className="inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-[10px] font-bold"
                  style={{ backgroundColor: withAlpha(t.color, 0.13), color: t.color }}
                >
                  <span className="size-1.5 rounded-full" style={{ backgroundColor: t.color }} />
                  {t.name}
                  <span className="opacity-60">{count}</span>
                </span>
              );
            })}
            {isSuperAdmin && (
              <button
                type="button"
                onClick={onManageTeams}
                className="inline-flex items-center gap-1 rounded-full border border-dashed border-ink-faint px-2 py-1 text-[10px] font-bold text-ink-soft transition-colors hover:border-accent hover:text-accent"
              >
                <Users className="size-3" />
                {teams.length === 0 ? "Add team" : "Manage"}
              </button>
            )}
          </div>
          {isPrivileged && !isSuperAdmin && (
            <div className="mt-2 flex items-center gap-1.5 rounded-lg border border-ink/10 bg-cream/70 px-2 py-1.5">
              <span className="grid size-5 shrink-0 place-items-center rounded-md bg-white shadow-xs text-[9px]">
                🔒
              </span>
              <div className="min-w-0">
                <div className="text-[9px] font-bold tracking-wide text-ink-soft uppercase leading-tight">
                  Super admin required
                </div>
                <div className="text-[9px] leading-tight text-ink-faint">
                  Only super admins can manage Field Teams.
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="min-h-0 flex-1 space-y-0.5 overflow-y-auto pr-0.5">
          {isPrivileged && !isSuperAdmin && (
            <div className="mx-1 mb-1.5 flex items-center gap-1.5 rounded-lg border border-ink/10 bg-cream/60 px-2 py-1.5">
              <span className="grid size-5 shrink-0 place-items-center rounded-md bg-white shadow-xs text-[9px]">
                🔒
              </span>
              <div className="min-w-0">
                <div className="text-[9px] font-bold tracking-wide text-ink-soft uppercase leading-tight">
                  Roster read-only
                </div>
                <div className="text-[9px] leading-tight text-ink-faint">
                  Only super admins can edit or remove employees.
                </div>
              </div>
            </div>
          )}
          {employees.map((e) => {
            const writable = canEditEmployee(e);
            return (
              <div
                key={e.id}
                className={cn(
                  "group flex items-center gap-2.5 rounded-lg px-2 py-2 transition-colors",
                  writable ? "hover:bg-black/[0.04]" : "opacity-90",
                )}
              >
                <EmployeeDot name={e.name} color={e.color} size={30} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className="truncate text-sm leading-tight font-semibold">{e.name}</span>
                    {!writable && (
                      <span
                        title={`Read-only — bound to ${e.boundAccountName ?? e.boundAccountEmail ?? "another account"}.`}
                        className="shrink-0 text-[10px] text-ink-faint"
                      >
                        🔒
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5">
                    {(() => {
                      const team = teams.find((t) => t.id === e.teamId);
                      return team ? (
                        <span
                          className="shrink-0 rounded px-1 py-px text-[9px] font-bold"
                          style={{ backgroundColor: withAlpha(team.color, 0.15), color: team.color }}
                        >
                          {team.name}
                        </span>
                      ) : null;
                    })()}
                    <span className="truncate text-[11px] text-ink-faint">
                      {e.role || "Team member"}
                    </span>
                  </div>
                </div>
                {writable && isSuperAdmin && (
                  <div className="flex items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
                    <button
                      type="button"
                      title="Edit employee"
                      onClick={() => onEditEmployee(e)}
                      className="grid size-7 place-items-center rounded-md text-ink-faint transition-colors hover:bg-ink/10 hover:text-ink"
                    >
                      <svg viewBox="0 0 24 24" className="size-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                        <path d="m15 5 4 4" />
                      </svg>
                    </button>
                    <ConfirmDelete onConfirm={() => onDeleteEmployee(e.id)} />
                  </div>
                )}
              </div>
            );
          })}
          {isSuperAdmin ? (
            <button
              type="button"
              onClick={onAddEmployee}
              className="mt-1.5 flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-ink-faint px-3 py-2.5 text-xs font-bold text-ink-soft transition-colors hover:border-accent hover:text-accent"
            >
              <Plus className="size-3.5" />
              Add employee
            </button>
          ) : isPrivileged ? (
            <div className="mt-1.5 flex w-full items-center gap-2 rounded-xl border border-ink/10 bg-cream/70 px-3 py-2.5">
              <span className="grid size-6 shrink-0 place-items-center rounded-lg bg-white shadow-xs text-[10px]">
                🔒
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-[10px] font-bold tracking-wide text-ink-soft uppercase">
                  Super admin required
                </div>
                <div className="text-[10px] leading-tight text-ink-faint">
                  Only super admins can add field employees.
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-1.5 flex w-full items-center gap-2 rounded-xl border border-ink/10 bg-cream/70 px-3 py-2.5">
              <span className="grid size-6 shrink-0 place-items-center rounded-lg bg-white shadow-xs text-[10px]">
                🔒
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-[10px] font-bold tracking-wide text-ink-soft uppercase">
                  Read-only access
                </div>
                <div className="text-[10px] leading-tight text-ink-faint">
                  Ask an admin to create and bind a profile.
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* footer */}
      <div className="px-4 pt-3 pb-5">
        {/* route svg + monthly stats card */}
        <div className="rounded-2xl border border-line bg-paper/70 p-3.5">
          <svg viewBox="0 0 220 56" className="w-full" aria-hidden>
            <path
              d="M6 46 C 46 10, 82 52, 118 24 S 194 30, 214 8"
              fill="none"
              stroke={withAlpha("#FACC15", 0.75)}
              strokeWidth="2"
              strokeDasharray="1 7"
              strokeLinecap="round"
            />
            <circle cx="6" cy="46" r="3.5" fill="#FACC15" />
            <circle cx="214" cy="8" r="3.5" fill="#1D4ED8" />
          </svg>
          <div className="mt-2 flex items-baseline justify-between">
            <span className="text-[10px] font-bold tracking-[0.18em] text-ink-faint uppercase">
              This month
            </span>
            <span className="font-display text-lg leading-none font-semibold">
              <RollingNumber value={monthCount} delay={0.5} />
              <span className="ml-1 font-sans text-[10px] font-medium text-ink-faint">
                / {itineraryCount} total
              </span>
            </span>
          </div>
        </div>
      </div>
    </aside>
  );
}

/** Mobile fallback navigation bar */
export function MobileNav({ tab, onTab }: { tab: TabId; onTab: (t: TabId) => void }) {
  return (
    <div className="sticky top-0 z-40 -mx-4 mb-4 border-b border-line bg-paper/85 px-4 py-3 backdrop-blur lg:hidden">
      <div className="flex items-center gap-3">
        <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-white p-1">
          <img src="/iSource LOGO1.png" alt="iSource" className="w-full h-full object-contain" />
        </span>
        <div className="hidden min-w-0 sm:block">
          <div className="font-display text-sm leading-none font-semibold tracking-tight">
            iSource RouteCore
          </div>
          <div className="mt-0.5 text-[8px] font-bold tracking-[0.16em] text-ink-faint uppercase">
            Team Itinerary System
          </div>
        </div>
        <span className="hidden h-6 w-px bg-line sm:block" />
        <div className="flex flex-1 items-center gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {NAV_ITEMS.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => onTab(item.id)}
              className={cn(
                "flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold transition-colors",
                tab === item.id ? "bg-ink text-porcelain" : "text-ink-soft hover:bg-black/5",
              )}
            >
              <item.icon className="size-3.5" />
              {item.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
